import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { BsBrowserEdge } from "react-icons/bs";
import { TbDatabaseImport } from "react-icons/tb";
import { RxDashboard } from "react-icons/rx";
import { 
  FaUsers, 
  FaBook, 
  FaUser, 
  FaUserCheck,
  FaChevronDown,
  FaChevronRight,
  FaFileAlt,
  FaClipboardList,
  FaChartLine,
  FaUserPlus,
  FaExchangeAlt,
  FaAward,
  FaBookOpen,
  FaLayerGroup,
  FaThLarge,
  FaDollarSign,
  FaCog,
  FaCreditCard,
  FaChartBar,
  FaEdit,
  FaGlobe,
  FaTools,
  FaSchool,
  FaUserNurse,
  FaFileInvoice,
  FaShieldAlt
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';

const Sidebar = ({ isOpen }) => {
  const { user } = useAuth();
  const [openDropdowns, setOpenDropdowns] = useState({});

  const toggleDropdown = (key) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getRoleIcon = (role) => {
    if (role === 'admin') return <FaShieldAlt />;
    if (role === 'staff') return <FaUserCheck />;
    if (role === 'ketua' || role?.includes('wakil_ketua')) return <FaShieldAlt />;
    if (role === 'ketua_prodi' || role === 'sekretaris_prodi') return <FaUserCheck />;
    return <FaUser />;
  };

  const getRoleBadgeClass = (role) => {
    if (role === 'admin') return 'role-badge-admin';
    if (role === 'staff') return 'role-badge-staff';
    if (role === 'ketua' || role?.includes('wakil_ketua')) return 'role-badge-leader';
    if (role?.includes('prodi') || role === 'baak' || role === 'bauk') return 'role-badge-academic';
    if (role === 'lpm' || role === 'lppm') return 'role-badge-quality';
    if (role === 'mahasiswa') return 'role-badge-student';
    if (role === 'calon_mahasiswa') return 'role-badge-prospect';
    return 'role-badge-user';
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      admin: 'ADMIN',
      staff: 'STAFF',
      viewer: 'VIEWER',
      admin_pmb: 'ADMIN PMB',
      ketua: 'KETUA',
      wakil_ketua_1: 'WAKET 1',
      wakil_ketua_2: 'WAKET 2',
      wakil_ketua_3: 'Wakil Ketua III',
      ketua_prodi: 'KAPRODI',
      sekretaris_prodi: 'SEKPRODI',
      lpm: 'LPM',
      lppm: 'LPPM',
      baak: 'BAAK',
      bauk: 'BAUK',
      mou: 'MOU',
      perlengkapan: 'PERLENGKAPAN',
      mahasiswa: 'MAHASISWA',
      calon_mahasiswa: 'CAMABA',
      kemahasiswaan: 'KEMAHASISWAAN'
    };
    return roleNames[role] || role?.toUpperCase() || 'USER';
  };

  const getAvatarUrl = (avatar) => {
    if (!avatar || avatar === 'default-avatar.png' || avatar === 'default-student.png') {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=random&size=200`;
    }
    
    // Check if avatar is student photo path (contains /)
    if (avatar.includes('/')) {
      return `http://localhost:5000/uploads/${avatar}`;
    }
    
    return `http://localhost:5000/uploads/avatars/${avatar}`;
  };

  // Menu items for BAAK role
  const baakMenuItems = [
    { path: '/baak/dashboard', icon: <RxDashboard />, label: 'Dashboard BAAK' },
    { path: '/baak/proposal', icon: <FaFileInvoice />, label: 'Proposal' },
    { 
      key: 'keuangan-baak',
      icon: <FaDollarSign />, 
      label: 'Keuangan',
      isDropdown: true,
      children: [
        { path: '/baak/setting-pembayaran', icon: <FaCog />, label: 'Setting Pembayaran' },
        { path: '/baak/pembayaran', icon: <FaCreditCard />, label: 'Pembayaran' },
        { path: '/baak/tagihan', icon: <FaFileAlt />, label: 'Tagihan' },
        { path: '/baak/status-pembayaran', icon: <FaClipboardList />, label: 'Status Pembayaran' },
        { path: '/baak/laporan', icon: <FaChartBar />, label: 'Laporan Keuangan' },
      ]
    },
  ];

  // Menu items for MAHASISWA role
  const mahasiswaMenuItems = [
    { path: '/dashboard', icon: <RxDashboard />, label: 'Dashboard' },
    { 
      key: 'akademik',
      icon: <FaBook />, 
      label: 'Akademik',
      isDropdown: true,
      children: [
        { path: '/mahasiswa/krs', icon: <FaFileAlt />, label: 'KRS' },
        { path: '/mahasiswa/khs', icon: <FaClipboardList />, label: 'KHS' },
        { path: '/mahasiswa/akm', icon: <FaChartLine />, label: 'AKM' },
        { path: '/mahasiswa/transkrip', icon: <FaAward />, label: 'Transkrip Nilai' },
        { path: '/mahasiswa/jadwal', icon: <FaThLarge />, label: 'Jadwal Kuliah' },
      ]
    },
    {
      key: 'keuangan',
      icon: <FaDollarSign />,
      label: 'Keuangan',
      isDropdown: true,
      children: [
        { path: '/mahasiswa/pembayaran', icon: <FaCreditCard />, label: 'Riwayat Pembayaran' },
        { path: '/mahasiswa/tagihan', icon: <FaDollarSign />, label: 'Tagihan' },
      ]
    },
    { path: '/mahasiswa/profile', icon: <FaUser />, label: 'Profil Saya' },
  ];

  // Menu items for KEMAHASISWAAN (Wakil Ketua 3) role
  const kemahasiswaanMenuItems = [
    { path: '/kemahasiswaan/dashboard', icon: <RxDashboard />, label: 'Dashboard' },
    { path: '/kemahasiswaan/proposal', icon: <FaFileInvoice />, label: 'Proposal Kegiatan' },
    { 
      key: 'kemahasiswaan-kegiatan',
      icon: <FaUsers />, 
      label: 'Kegiatan Mahasiswa',
      isDropdown: true,
      children: [
        { path: '/kemahasiswaan/kegiatan-ormawa', icon: <FaUsers />, label: 'Kegiatan Ormawa' },
        { path: '/kemahasiswaan/kegiatan-ukm', icon: <FaAward />, label: 'Kegiatan UKM' },
        { path: '/kemahasiswaan/prestasi', icon: <FaAward />, label: 'Prestasi Mahasiswa' },
      ]
    },
    {
      key: 'kemahasiswaan-keuangan',
      icon: <FaDollarSign />,
      label: 'Keuangan',
      isDropdown: true,
      children: [
        { path: '/kemahasiswaan/dana-kemahasiswaan', icon: <FaDollarSign />, label: 'Dana Kemahasiswaan' },
        { path: '/kemahasiswaan/pencairan-dana', icon: <FaCreditCard />, label: 'Pencairan Dana' },
        { path: '/kemahasiswaan/laporan-keuangan', icon: <FaChartBar />, label: 'Laporan Keuangan' },
      ]
    },
    {
      key: 'kemahasiswaan-organisasi',
      icon: <FaUserCheck />,
      label: 'Organisasi',
      isDropdown: true,
      children: [
        { path: '/kemahasiswaan/ormawa', icon: <FaUsers />, label: 'Data Ormawa' },
        { path: '/kemahasiswaan/ukm', icon: <FaAward />, label: 'Data UKM' },
        { path: '/kemahasiswaan/pengurus', icon: <FaUserCheck />, label: 'Data Pengurus' },
      ]
    },
    { path: '/kemahasiswaan/laporan', icon: <FaChartBar />, label: 'Laporan' },
  ];

  // Menu items for ADMIN/STAFF/OTHER roles
  const menuItems = [
    { path: '/dashboard', icon: <RxDashboard />, label: 'Dashboard' },
    { path: '/profil-pt', icon: <FaSchool/>, label: 'Profil PT' },
    { 
      key: 'akademik',
      icon: <FaBook />, 
      label: 'Data Akademik',
      isDropdown: true,
      children: [
        {
          key: 'mahasiswa',
          icon: <FaUsers />,
          label: 'Mahasiswa',
          isDropdown: true,
          children: [
            { path: '/students', icon: <FaUserNurse/>, label: 'Data Mahasiswa' },
            { path: '/krs', icon: <FaFileAlt />, label: 'KRS Mahasiswa' },
            { path: '/khs', icon: <FaClipboardList />, label: 'KHS Mahasiswa' },
            { path: '/akm', icon: <FaChartLine />, label: 'AKM Mahasiswa' },
            { path: '/nilai-transfer', icon: <FaExchangeAlt />, label: 'Nilai Transfer' },
            { path: '/transkrip-nilai', icon: <FaAward />, label: 'Transkrip Nilai' },
          ]
        },
        { path: '/kelas', icon: <FaThLarge />, label: 'Kelas' },
      ]
    },
    {
      key: 'sdm',
      icon: <FaUser />,
      label: 'Data SDM',
      isDropdown: true,
      children: [
        { path: '/lecturers', icon: <FaUserCheck />, label: 'Dosen' },
        { path: '/staff', icon: <FaUserPlus />, label: 'Staff' },
      ]
    },
    {
      key: 'kurikulum',
      icon: <FaBookOpen />,
      label: 'Kurikulum',
      isDropdown: true,
      children: [
        {
          key: 'prodi',
          icon: <FaLayerGroup />,
          label: 'Program Studi',
          isDropdown: true,
          children: [
            { path: '/kurikulum/profesi-ners', icon: <FaBookOpen />, label: 'Profesi Ners' },
            { path: '/kurikulum/s1-keperawatan', icon: <FaBookOpen />, label: 'S1 Keperawatan' },
            { path: '/kurikulum/s1-kesehatan-masyarakat', icon: <FaBookOpen />, label: 'S1 Kesehatan Masyarakat' },
          ]
        },
        { path: '/courses', icon: <FaBook />, label: 'Mata Kuliah' },
      ]
    },
    {
      key: 'keuangan',
      icon: <FaDollarSign />,
      label: 'Keuangan',
      isDropdown: true,
      children: [
        { path: '/keuangan/setting-pembiayaan', icon: <FaCog />, label: 'Setting Pembiayaan' },
        { path: '/keuangan/pembayaran-mahasiswa', icon: <FaCreditCard />, label: 'Pembayaran Mahasiswa' },
        { path: '/keuangan/manajemen', icon: <FaChartBar />, label: 'Manajemen' },
      ]
    },
    {
      key: 'pmb',
      icon: <FaUserPlus />,
      label: 'PMB',
      isDropdown: true,
      children: [
        {
          key: 'pengaturan',
          icon: <FaCog />,
          label: 'Pengaturan',
          isDropdown: true,
          children: [
            { path: '/pmb/formulir-pendaftaran', icon: <FaEdit />, label: 'Formulir Pendaftaran' },
            { path: '/pmb/user', icon: <FaUser />, label: 'User' },
            { path: '/pmb/halaman', icon: <FaGlobe />, label: 'Halaman PMB' },
          ]
        },
      ]
    },
  ];


  // Add Neo Feeder dropdown menu for admin and staff
  if (user?.role === 'admin' || user?.role === 'staff') {
    menuItems.push({
      key: 'neoFeeder',
      icon: <BsBrowserEdge />,
      label: 'Neo Feeder',
      isDropdown: true,
      children: [
        { path: '/neofeeder', icon: <TbDatabaseImport/>, label: 'Tarik Data' }
      ]
    });
  }

  // Add Maintenance menu for admin
  if (user?.role === 'admin') {
    menuItems.push({
      key: 'maintenance',
      icon: <FaTools />,
      label: 'Maintenance',
      isDropdown: true,
      children: [
        { path: '/maintenance/website', icon: <FaGlobe />, label: 'Website' },
        { path: '/maintenance/mahasiswa', icon: <FaUsers />, label: 'Mahasiswa' },
        { path: '/maintenance/kelas', icon: <FaThLarge />, label: 'Kelas' },
      ]
    });
  }

  // Add Users menu only for admin
  if (user?.role === 'admin') {
    menuItems.push({ path: '/users', icon: <FaUser />, label: 'Users' });
  }

  const renderMenuItem = (item, level = 0) => {
    if (item.isDropdown) {
      const isOpen = openDropdowns[item.key];
      
      return (
        <div key={item.key} className={`nav-dropdown level-${level}`}>
          <div 
            className="nav-item dropdown-toggle" 
            onClick={() => toggleDropdown(item.key)}
          >
            {item.icon}
            <span>{item.label}</span>
            {isOpen ? <FaChevronDown className="dropdown-icon" /> : <FaChevronRight className="dropdown-icon" />}
          </div>
          
          {isOpen && (
            <div className="dropdown-menu">
              {item.children.map(child => renderMenuItem(child, level + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <NavLink
        key={item.path}
        to={item.path}
        className={({ isActive }) => `nav-item ${isActive ? 'active' : ''} level-${level}`}
      >
        {item.icon}
        <span>{item.label}</span>
      </NavLink>
    );
  };

  return (
    <aside className={`sidebar ${!isOpen ? 'closed' : ''}`}>
      {/* Profile Section */}
      <div className="sidebar-profile">
        <div className="profile-avatar">
          <img 
            src={getAvatarUrl(user?.avatar)}
            alt={user?.name || 'User'}
            onError={(e) => {
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=random&size=200`;
            }}
          />
        </div>
        <div className="profile-info">
          <h3 className="profile-name">{user?.name || 'User'}</h3>
          <div className={`profile-role ${getRoleBadgeClass(user?.role)}`}>
            {getRoleIcon(user?.role)}
            <span>{getRoleDisplayName(user?.role)}</span>
          </div>
        </div>
      </div>

      <div className="sidebar-divider"></div>
      
      {/* Brand Section */}
      <div className="sidebar-brand">
        <div className="brand-icon">
          <FaSchool />
        </div>
        <div className="brand-text">
          <h2>
            {user?.role === 'baak' ? 'PORTAL BAAK' : 
             user?.role === 'wakil_ketua_3' || user?.role === 'kemahasiswaan' ? 'PORTAL KEMAHASISWAAN' :
             user?.role === 'mahasiswa' ? 'PORTAL MAHASISWA' : 
             'ADMIN PORTAL'}
          </h2>
          <p>STIKES Baramuli</p>
        </div>
      </div>

      <div className="sidebar-divider"></div>
      
      <nav className="sidebar-nav">
        {/* Render menu based on user role */}
        {user?.role === 'baak'
          ? baakMenuItems.map((item) => renderMenuItem(item))
          : user?.role === 'wakil_ketua_3' || user?.role === 'kemahasiswaan'
            ? kemahasiswaanMenuItems.map((item) => renderMenuItem(item))
            : user?.role === 'mahasiswa' 
              ? mahasiswaMenuItems.map((item) => renderMenuItem(item))
              : menuItems.map((item) => renderMenuItem(item))
        }
      </nav>
    </aside>
  );
};

export default Sidebar;
